import React, { useEffect, useState } from 'react';
import { Table, Tag, Select, message, Card, Statistic, Row, Col, Modal, Radio, Button, DatePicker, Space, Popconfirm, Tabs, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// Kış dönemi 8 ay sürüyor; tüm dönemi tek listede göstermek takibi
// zorlaştırıyor. Varsayılan görünüm bu yüzden bugünün etrafındaki dar bir
// pencere: geçmiş 1 ay + gelecek 1 ay. Tüm dönemi görmek isteyen "Tüm Dönem"
// sekmesine geçer, orada eski davranış (serbest tarih aralığı) aynen çalışır.
const RECENT_MONTHS_BACK    = 1;
const RECENT_MONTHS_FORWARD = 1;

const STATUS_LABEL = { present: '✓ Geldi', absent: '✗ Gelmedi', planned_absent: 'Mazeretli Devamsız', planned_absent_paid: 'Gelmeyecek', missing: 'Eksik' };
const STATUS_COLOR = { present: 'green', absent: 'red', planned_absent: 'purple', planned_absent_paid: 'magenta', missing: 'orange' };

const StudentAttendanceTab = ({ studentId, enrollments, readOnly = false }) => {
  // Term select
  const [selectedTermEnrollmentId, setSelectedTermEnrollmentId] = useState(null);

  // Sessions
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const PAGE_SIZE = 20;

  // Date range filter
  const [dateRange, setDateRange] = useState(null);
  // 'recent' = bugünün etrafındaki ±1 ay (varsayılan), 'all' = tüm dönem
  const [viewMode, setViewMode] = useState('recent');

  // Attendance modal
  const [attendanceModal, setAttendanceModal]   = useState(false);
  const [selectedSession, setSelectedSession]   = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Build term enrollment list from enrollments prop
  const termEnrollments = (enrollments || []).map(e => ({
    id:       e.id,
    termName: e.term?.name || `Dönem #${e.term_id}`,
    isActive: e.is_active,
  }));

  // Set default to first active term enrollment
  useEffect(() => {
    if (termEnrollments.length && !selectedTermEnrollmentId) {
      const active = termEnrollments.find(te => te.isActive) || termEnrollments[0];
      setSelectedTermEnrollmentId(active.id);
    }
  }, [enrollments]);

  useEffect(() => {
    if (selectedTermEnrollmentId) { setPage(1); fetchSessions(1); }
    else setSessions([]);
  }, [studentId, selectedTermEnrollmentId, dateRange, viewMode]);

  const fetchSessions = async (pg = page) => {
    setLoading(true);
    try {
      const params = {
        term_enrollment_id: selectedTermEnrollmentId,
        per_page: PAGE_SIZE,
        page: pg,
      };
      // "Yakın Dönem" sekmesinde aralık sabit ve kullanıcı tarafından
      // değiştirilemez; "Tüm Dönem"de eskisi gibi RangePicker belirler
      // (seçim yoksa dönemin tamamı gelir).
      const range = viewMode === 'recent'
        ? [dayjs().subtract(RECENT_MONTHS_BACK, 'month'), dayjs().add(RECENT_MONTHS_FORWARD, 'month')]
        : dateRange;
      if (range?.[0]) params.date_from = range[0].format('YYYY-MM-DD');
      if (range?.[1]) params.date_to   = range[1].format('YYYY-MM-DD');
      const res = await api.get('/lesson-sessions', { params });
      setSessions(res.data.data || []);
      setTotal(res.data.meta?.total || 0);
    } catch {
      message.error('Dersler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAttendance = (session) => {
    const existing = session.attendances?.find(a => a.student_id === parseInt(studentId));
    setSelectedSession(session);
    setAttendanceStatus(existing?.status || null);
    setAttendanceModal(true);
  };

  const handleSaveAttendance = async () => {
    if (!attendanceStatus) { message.error('Yoklama durumu seçiniz.'); return; }
    setSavingAttendance(true);
    try {
      await api.post(`/lesson-sessions/${selectedSession.id}/attendances`, {
        attendances: [{ student_id: parseInt(studentId), status: attendanceStatus }],
      });
      message.success('Yoklama kaydedildi.');
      setAttendanceModal(false);
      fetchSessions(page);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Yoklama kaydedilemedi.'));
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleDeleteAttendance = async () => {
    const existing = selectedSession.attendances?.find(a => a.student_id === parseInt(studentId));
    if (!existing) return;
    setSavingAttendance(true);
    try {
      await api.delete(`/attendances/${existing.id}`);
      message.success('Yoklama kaldırıldı.');
      setAttendanceModal(false);
      fetchSessions(page);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Yoklama kaldırılamadı.'));
    } finally {
      setSavingAttendance(false);
    }
  };

  const attendance = sessions.reduce((acc, s) => {
    const a = s.attendances?.find(a => a.student_id === parseInt(studentId));
    if (a) acc[a.status] = (acc[a.status] || 0) + 1;
    else acc.missing = (acc.missing || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'date',
      render: (v) => dayjs(v).format('DD.MM.YYYY'),
      // Istemci sıralamasi bilerek KALDIRILDI: bu liste sunucudan sayfali
      // geliyor, dolayisiyla buradaki sorter yalnizca gorunen sayfayi
      // siralar - kullanici "siraladim" sanir ama listenin tamami sirali
      // degildir. Sunucu artik tarih + saat + id'ye gore kesin sirada
      // donduruyor (LessonSessionController::index), o yuzden liste zaten
      // dogru sirada.
    },
    {
      title: 'Saat',
      render: (_, r) => `${r.start_time?.substring(0, 5)} — ${r.end_time?.substring(0, 5)}`,
    },
    {
      title: 'Sınıf',
      render: (_, r) => r.classroom?.name || '—',
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      render: (v) => (
        <Tag color={v === 'makeup' ? 'orange' : v === 'trial' ? 'gold' : 'blue'}>
          {v === 'makeup' ? 'Telafi' : v === 'trial' ? 'Deneme' : 'Normal'}
        </Tag>
      ),
    },
    {
      title: 'Proje / Konu',
      render: (_, record) => {
        const projs = record.student_projects?.filter(p => p.student_id === parseInt(studentId)) || [];
        if (!projs.length) return <span className="text-gray-400 text-xs">—</span>;
        return (
          <div className="text-xs space-y-0.5">
            {projs.map((proj, i) => (
              <div key={i}>
                <span className="font-medium">{proj.course?.name}</span>
                {proj.topic && <span className="text-gray-500"> · {proj.topic.name}</span>}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Yoklama',
      render: (_, record) => {
        const a = record.attendances?.find(a => a.student_id === parseInt(studentId));
        if (!a) return <Tag color="default">Alınmadı</Tag>;
        return <Tag color={STATUS_COLOR[a.status] || 'default'}>{STATUS_LABEL[a.status] || a.status}</Tag>;
      },
    },
    ...(!readOnly ? [{
      title: 'İşlem',
      render: (_, record) => (
        <Button size="small" onClick={() => handleOpenAttendance(record)}>
          {record.attendances?.find(a => a.student_id === parseInt(studentId)) ? 'Düzenle' : 'Yoklama Al'}
        </Button>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* Görünüm sekmeleri — sekme değişince aynı içerik farklı tarih
          aralığıyla yeniden yükleniyor, ayrı bir ekran yok. */}
      <Tabs
        activeKey={viewMode}
        onChange={setViewMode}
        items={[
          { key: 'recent', label: 'Yakın Dönem' },
          { key: 'all',    label: 'Tüm Dönem' },
        ]}
      />

      {/* Filtreler */}
      <Space className="mb-4" wrap>
        {termEnrollments.length > 0 && (
          <Select
            style={{ minWidth: 220 }}
            placeholder="Dönem seçiniz"
            value={selectedTermEnrollmentId}
            onChange={(v) => { setSelectedTermEnrollmentId(v); setDateRange(null); }}
          >
            {termEnrollments.map(te => (
              <Option key={te.id} value={te.id}>
                {te.termName}
                {te.isActive && <Tag color="green" className="ml-2" style={{ fontSize: 10 }}>Aktif</Tag>}
              </Option>
            ))}
          </Select>
        )}
        {viewMode === 'all' ? (
          <>
            <RangePicker
              format="DD.MM.YYYY"
              value={dateRange}
              onChange={setDateRange}
              placeholder={['Başlangıç', 'Bitiş']}
              allowClear
            />
            <Button icon={<SearchOutlined />} onClick={() => fetchSessions(1)}>Ara</Button>
          </>
        ) : (
          <Text type="secondary">
            {dayjs().subtract(RECENT_MONTHS_BACK, 'month').format('DD.MM.YYYY')} —{' '}
            {dayjs().add(RECENT_MONTHS_FORWARD, 'month').format('DD.MM.YYYY')} arası gösteriliyor.
            Dönemin tamamı için “Tüm Dönem” sekmesine geçin.
          </Text>
        )}
      </Space>

      {/* Özet */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Toplam Ders" value={total || sessions.length} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Geldi" value={attendance.present || 0} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Gelmedi" value={attendance.absent || 0} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Mazeretli Devamsız" value={attendance.planned_absent || 0} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={sessions}
        rowKey="id"
        loading={loading}
        pagination={{
          current:  page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (t) => `Toplam ${t} ders`,
          onChange: (pg) => { setPage(pg); fetchSessions(pg); },
        }}
        locale={{ emptyText: selectedTermEnrollmentId ? 'Bu dönemde ders bulunamadı.' : 'Dönem seçiniz.' }}
      />

      {/* Yoklama Modal */}
      <Modal
        title={`Yoklama — ${dayjs(selectedSession?.date).format('DD.MM.YYYY')} ${selectedSession?.start_time?.substring(0, 5)}`}
        open={attendanceModal}
        onCancel={() => setAttendanceModal(false)}
        footer={
          <div className="flex justify-between">
            <div>
              {selectedSession?.attendances?.find(a => a.student_id === parseInt(studentId)) && (
                <Popconfirm
                  title="Yoklama kaydı silinsin mi?"
                  onConfirm={handleDeleteAttendance}
                  okText="Evet, Kaldır"
                  cancelText="Hayır"
                >
                  <Button danger loading={savingAttendance}>Yoklamayı Kaldır</Button>
                </Popconfirm>
              )}
            </div>
            <Space>
              <Button onClick={() => setAttendanceModal(false)}>İptal</Button>
              <Button type="primary" loading={savingAttendance} onClick={handleSaveAttendance}>Kaydet</Button>
            </Space>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Yoklama Durumu</label>
            <Radio.Group value={attendanceStatus} onChange={e => setAttendanceStatus(e.target.value)} buttonStyle="solid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, width: '100%' }}>
              <Radio.Button value="present" style={{ textAlign: 'center' }}>✓ Geldi</Radio.Button>
              <Radio.Button value="absent" style={{ textAlign: 'center' }}>✗ Gelmedi</Radio.Button>
              <Radio.Button value="planned_absent" style={{ textAlign: 'center' }}>📅 Mazeretli</Radio.Button>
              <Radio.Button value="planned_absent_paid" style={{ textAlign: 'center' }}>🚫 Gelmeyecek</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentAttendanceTab;
